import { MembraneSrnfSchema as BackendSrnfMembraneSchema } from "../../../../../backend/models/membrane-srnf.model"
import { useQuery } from '@tanstack/react-query'
import { fetchMembrane, fetchMembranes, fetchMembranesSchema, Membrane, MembraneFilters, MembranePaths, MembraneSchema } from '../membranes-queries'
import { RoMembranePaths } from "../ro/ro-queries"
import { CommonPaths } from "../../omd"
import { cloneDeep } from "lodash"

export type SrnfMembrane = Membrane<BackendSrnfMembraneSchema>

export type SrnfMembranePaths = MembranePaths<SrnfMembrane>

export type SrnfMembraneAllowedPaths = CommonPaths<
  SrnfMembranePaths,
  'characterizationResults.characterizationData' |
  'characterizationResults.contactAngle' |
  'characterizationResults.totalThickness' |
  'filtrationResults.mwco' |
  'filtrationResults.permeance' |
  'filtrationResults.solventPermeance' |
  'modification' |
  'supportLayerChemistry' |
  'testConditions.filtrationMode' |
  'testConditions.hydraulicP' |
  'testConditions.solvent1' |
  'testConditions.solvent2' |
  'testConditions.temperature' |
  "chemistry" |
  "report.publicationDate" |
  "structure" |
  "type"
>
  | 'soluteChoice.solute.molecularWeight'
  | 'soluteChoice.solute.category'
  | 'soluteChoice.solute.name'
  | 'filtrationResults.rejection'

// All filters available to the user for SRNF Membranes
export const srnfMembranesAllowedPaths: SrnfMembraneAllowedPaths[] =
  [
    'characterizationResults.characterizationData',
    'characterizationResults.contactAngle',
    'characterizationResults.totalThickness',
    'filtrationResults.mwco',
    'filtrationResults.permeance',
    'filtrationResults.rejection',
    'filtrationResults.solventPermeance',
    'modification',
    'report.publicationDate',
    'soluteChoice.solute.category',
    'soluteChoice.solute.molecularWeight',
    'soluteChoice.solute.name',
    'supportLayerChemistry',
    'testConditions.filtrationMode',
    'testConditions.hydraulicP',
    'testConditions.solvent1',
    'testConditions.solvent2',
    'testConditions.temperature',
    'type',
    "chemistry",
    "structure",
  ] as const

export type SrnfMembranesFilters = MembraneFilters<SrnfMembrane, SrnfMembraneAllowedPaths> & { 'filter'?: string }

export type SrnfMembraneSchema = MembraneSchema<SrnfMembraneAllowedPaths | 'soluteChoice.solute.molecularWeight'>

export type SrnfMembranesQueryKey = 'srnf-membranes'
export const queryKey: SrnfMembranesQueryKey = 'srnf-membranes'
export const srnfMembranesQueryPath = '/membranes-srnf'
export type SrnfMembranesSchemaQueryKey = 'srnf-membranes-schema'
export const schemaQueryKey: SrnfMembranesSchemaQueryKey = 'srnf-membranes-schema'
export const srnfMembranesSchemaQueryPath = srnfMembranesQueryPath + '/schema'

export const fetchSrnfMembranes = (params?: SrnfMembranesFilters) => {
  let newParams = cloneDeep(params)
  let filter = { $or: [] as { $and: object[] }[] }
  if (
    newParams && (
      'soluteChoice.solute.molecularWeight' in newParams ||
      'soluteChoice.solute.name' in newParams ||
      'soluteChoice.solute.category' in newParams ||
      'filtrationResults.rejection' in newParams
    ) && (
      newParams['soluteChoice.solute.molecularWeight'] ||
      newParams['soluteChoice.solute.name'] ||
      newParams['soluteChoice.solute.category'] ||
      newParams['filtrationResults.rejection']
    )
  ) {
    filter.$or = [...Array(15).keys()].map(i => ({ $and: [] }))
    if (newParams && 'soluteChoice.solute.molecularWeight' in newParams) {
      if (typeof newParams['soluteChoice.solute.molecularWeight'] === 'object') {
        if (newParams['soluteChoice.solute.molecularWeight']) {
          const min = 'min' in newParams['soluteChoice.solute.molecularWeight'] ? newParams['soluteChoice.solute.molecularWeight'].min : undefined
          const max = 'max' in newParams['soluteChoice.solute.molecularWeight'] ? newParams['soluteChoice.solute.molecularWeight'].max : undefined
          filter['$or'].map((and, i) => {
            and.$and.push({ [`soluteChoice.solute${i + 1}.molecularWeight`]: { $gte: min, $lte: max } })
            return and
          })
        }
      }
      delete newParams['soluteChoice.solute.molecularWeight']
    }
    if (newParams && 'filtrationResults.rejection' in newParams) {
      if (typeof newParams['filtrationResults.rejection'] === 'object') {
        if (newParams['filtrationResults.rejection']) {
          const min = 'min' in newParams['filtrationResults.rejection'] ? newParams['filtrationResults.rejection'].min : undefined
          const max = 'max' in newParams['filtrationResults.rejection'] ? newParams['filtrationResults.rejection'].max : undefined
          filter['$or'].map((and, i) => {
            and.$and.push({ [`filtrationResults.rejection${i + 1}`]: { $gte: min, $lte: max } })
            return and
          })
        }
      }
      delete newParams['filtrationResults.rejection']
    }
    if (newParams && 'soluteChoice.solute.category' in newParams) {
      if (Array.isArray(newParams['soluteChoice.solute.category']) && newParams['soluteChoice.solute.category'].length > 0) {
        filter['$or'].map((and, i) => {
          and.$and.push({ [`soluteChoice.solute${i + 1}.category`]: newParams!['soluteChoice.solute.category'] })
        })
      }
      delete newParams['soluteChoice.solute.category']
    }
    if (newParams && 'soluteChoice.solute.name' in newParams) {
      if (Array.isArray(newParams['soluteChoice.solute.name']) && newParams['soluteChoice.solute.name'].length > 0) {
        console.log(newParams['soluteChoice.solute.name'])
        filter['$or'].map((and, i) => {
          and.$and.push({ [`soluteChoice.solute${i + 1}.name`]: newParams!['soluteChoice.solute.name'] })
        })
      }
      delete newParams['soluteChoice.solute.name']
    }
  }
  if (filter.$or.length > 0) {
    if (newParams === undefined) newParams = {}
    newParams['filter'] = JSON.stringify(filter)
  }
  return fetchMembranes<SrnfMembrane, SrnfMembranesFilters>('/membranes-srnf', newParams)
}
export const fetchSrnfMembranesSchema = async () => {
  const srnfMembranesSchema = await fetchMembranesSchema<SrnfMembraneSchema>('/membranes-srnf/schema')

  srnfMembranesSchema['filtrationResults.rejection'] = {
    // @ts-expect-error is not in Allowed Paths
    ...srnfMembranesSchema['filtrationResults.rejection1'],
    path: 'filtrationResults.rejection',
    labels: {
      // @ts-expect-error is not in Allowed Paths
      ...srnfMembranesSchema['filtrationResults.rejection1'].labels,
      short: 'Observed rejection',
      long: 'Observed rejection',
    },
  }

  srnfMembranesSchema['soluteChoice.solute.molecularWeight'] = {
    // @ts-expect-error is not in Allowed Paths
    ...srnfMembranesSchema['soluteChoice.solute1.molecularWeight'],
    path: 'soluteChoice.solute.molecularWeight',
    labels: {
      // @ts-expect-error is not in Allowed Paths
      ...srnfMembranesSchema['soluteChoice.solute1.molecularWeight'].labels,
      short: 'Solute MW',
      long: 'Molecular Weight of solute'
    },
    validations: {}
  }

  srnfMembranesSchema['soluteChoice.solute.category'] = {
    // @ts-expect-error is not in Allowed Paths
    ...srnfMembranesSchema['soluteChoice.solute1.category'],
    path: 'soluteChoice.solute.category',
    labels: {
      // @ts-expect-error is not in Allowed Paths
      ...srnfMembranesSchema['soluteChoice.solute1.category'].labels,
      short: 'Solute category',
      long: 'Category of solute'
    },
  }
  srnfMembranesSchema['soluteChoice.solute.name'] = {
    // @ts-expect-error is not in Allowed Paths
    ...srnfMembranesSchema['soluteChoice.solute1.name'],
    path: 'soluteChoice.solute.name',
    labels: {
      // @ts-expect-error is not in Allowed Paths
      ...srnfMembranesSchema['soluteChoice.solute1.name'].labels,
      short: 'Solute name',
      long: 'Name of solute'
    },
    validations: {
      enum: Object.values(SoluteName) // MASSIVE HACK
    }
  }
  return srnfMembranesSchema
}

export const fetchSrnfMembrane = (id: BackendSrnfMembraneSchema['_id']) => {
  return fetchMembrane<SrnfMembrane>(`/membranes-srnf/${id}`)
}

export const useSrnfMembranesQuery = (params?: SrnfMembranesFilters) => useQuery(
  [queryKey, params],
  () => fetchSrnfMembranes({ ...params/* , _id: '66042da9b6f18181f85ebca8' */ }),
)

export const useSrnfMembraneQuery = (id: BackendSrnfMembraneSchema['_id']) => useQuery(
  ['srnf-membrane', [id]],
  () => fetchSrnfMembrane(id),
)

/**
 * MASSIVE HACK -> source this from schema
 */
export enum SoluteName {
  notApplicable = 'Not Applicable',
  fluorescein4Isothiocyanate = 'Fluorescein-4-isothiocyanate',
  acidFuchsine = 'Acid fuchsine ',
  acidOrange7 = 'Acid orange 7',
  acidYellow14 = 'Acid yellow 14',
  acidYellow79 = 'Acid yellow 79',
  acridineOrange = 'Acridine orange',
  alcianBlue8Gx = 'Alcian blue 8GX',
  amidoBlack = 'Amido black',
  azobenzene = 'Azobenzene',
  briljantBlueFcf = 'Briljant blue FCF',
  briljantBlueG = 'Briljant blue G',
  briljantBlueR250 = 'Briljant blue R250',
  briljantCyaninG = 'Briljant cyaninG',
  brilliantBlueR = 'Brilliant blue R',
  bromocresolGreen = 'Bromocresol green',
  bromopyrogallolRed = 'Bromopyrogallol red',
  bromothymolBlue = 'Bromothymol Blue',
  chlorophyllin = 'Chlorophyllin',
  chromeBlackT = 'Chrome black T',
  chrysoidineG = 'Chrysoidine G',
  congoRed = 'Congo red',
  coomassieBriljantBlue = 'Coomassie briljant blue',
  cresolRed = 'Cresol red',
  crystalViolet = 'Crystal Violet ',
  directRed80 = 'Direct red 80',
  disperseBlack9 = 'Disperse black 9',
  disperseOrange1 = 'Disperse orange 1',
  disperseOrange25 = 'Disperse orange 25',
  disperseRed1 = 'Disperse red 1',
  disperseRed1Methacrylate = 'Disperse red 1 methacrylate',
  disperseRed13 = 'Disperse red 13',
  disperseRed13Methacrylate = 'Disperse red 13 methacrylate',
  disperseRed19 = 'Disperse red 19',
  eosinY = 'Eosin Y',
  eriochromeBlueBlackR = 'Eriochrome blue Black R',
  erythrosinB = 'Erythrosin B',
  evansBlue = 'Evans blue',
  fastGreenFcfDye = 'Fast green FCF dye',
  indigoCarmine = 'Indigo carmine',
  janusGreen = 'Janus green',
  methylBlue = 'Methyl blue',
  methylGreen = 'Methyl green ',
  methylOrange = 'Methyl orange',
  methylRed = 'Methyl red',
  methyleneBlue = 'Methylene blue',
  naturalRed = 'Natural red',
  neutralRed = 'Neutral red',
  nileRed = 'Nile red',
  oilRed = 'Oil red',
  orangeG = 'Orange G',
  orangeIi = 'Orange II',
  patentBlueVf = 'Patent Blue VF',
  primuline = 'Primuline',
  protoporphyrinIx = 'Protoporphyrin-IX',
  reactiveBlack = 'Reactive black',
  reactiveGreen = 'Reactive green',
  reactiveGreen_19 = 'Reactive green 19',
  reactiveOrange_16 = 'Reactive orange 16',
  reactiveRed_120 = 'Reactive red 120',
  remazolBriljantBlueR = 'Remazol briljant blue R',
  rhodamine_6G = 'Rhodamine 6G',
  rhodamineB = 'Rhodamine B',
  roseBengal = 'Rose Bengal',
  safranineO = 'Safranine O',
  safranineT = 'Safranine T',
  solventBlue_35 = 'Solvent blue 35',
  solventBlueIi = 'Solvent blue II',
  solventGreen_3 = 'Solvent green 3',
  solventYellow_7 = 'Solvent Yellow 7',
  sudan_408 = 'Sudan 408',
  sudanBlack = 'Sudan black',
  sudanBlackB = 'Sudan Black B',
  sudanBlue = 'Sudan blue',
  sudanIi = 'Sudan II',
  sudanIv = 'Sudan IV',
  sudanOrange = 'Sudan orange',
  sudanRed_7B = 'Sudan Red 7B',
  sunsetYellow = 'Sunset yellow ',
  tetrabromophenolBlue = 'Tetrabromophenol blue',
  thymolBlue = 'Thymol blue',
  titanYellow = 'Titan yellow',
  vitaminB12 = 'Vitamin B12',
  jacobsenCatalyst = 'Jacobsen Catalyst',
  pdBinap = 'Pd-BINAP',
  wilkinsonCatalyst = 'Wilkinson catalyst',
  nDecane = 'n-Decane',
  oneMethylNaphthalene = '1-Methyl-naphthalene',
  nHexadecane = 'N-Hexadecane',
  onePhenylundecane = '1-Phenylundecane',
  pristane = 'Pristane',
  nDocosane = 'n-Docosane',
  dodecane = 'Dodecane',
  tetradecane = 'Tetradecane ',
  hexadecane = 'Hexadecane',
  hexacosane = 'Hexacosane',
  tetraoctylammoniumBromide = 'Tetraoctylammonium bromide',
  octadecan = 'Octadecan',
  docosane = 'Docosane',
  tetracosane = 'Tetracosane',
  octacosane = 'Octacosane',
  hexaphenylbenzene = 'Hexaphenylbenzene',
  nNDimethylaminopyridine = 'N,N-dimethylaminopyridine',
  naphtalene = 'Naphtalene ',
  cisStilbene = 'Cis-stilbene',
  bisphenolA = 'Bisphenol A',
  triphenylMethanol = 'Triphenyl methanol',
  tetrahydroxyTetramethylSpriobisindane = '5,5\',6,6\'-tetrahydroxy-3,3,3\',3\'-tetramethyl-1,1\'-spriobisindane',
  roxithromycin = 'Roxithromycin',
  myristicAcid = 'Myristic acid',
  stearicAcid = 'Stearic acid',
  docosanicAcid = 'Docosanic acid',
  butylAcetate = 'Butyl acetate',
  hexylAcetate = 'Hexyl acetate',
  octylAcetate = 'Octyl acetate',
  dodecylAcetate = 'Dodecyl acetate',
  twoEthylbutylAcetate = '2-ethylbutyl acetate',
  twoEthylhexylAcetate = '2-ethylhexyl acetate',
  twoIsopropyl_5MethylhexylAcetate = '2-isopropyl-5-methylhexyl acetate',
  cyclohexylAcetate = 'Cyclohexyl acetate',
  fourTertButylCyclohexylAcetate = '4-tert-butyl-cyclohexyl acetate',
  dextrose = 'Dextrose',
  sucrose = 'Sucrose',
  maltose = 'Maltose',
  glucose = 'Glucose',
  saccharose = 'Saccharose',
  raffinose = 'Raffinose',
  aCyclodextrin = 'Α-cyclodextrin',
  tetrabutylammoniumBromide = 'Tetrabutylammonium bromide',
  mepenzolateBromide = 'Mepenzolate bromide',
  tetracycline = 'Tetracycline',
  paracetamol = 'Paracetamol',
  ceftazidime = 'Ceftazidime',
  lAPhosphatidylcholineLALecithin = 'L-a-Phosphatidylcholine (L-α-lecithin)',
  erythromycin = 'Erythromycin',
  betaCarotene = 'Beta-carotene',
  clarithromycin = 'Clarithromycin',
  Polystyrene = 'Polystyrene',
  aMethylstyreneDimer = 'a-methylstyrene dimer',
  divinylBenzene = 'divinyl benzene',
  Polyethyleneglycol = 'Polyethyleneglycol',
  Ethyleneglycol = 'Ethyleneglycol',
  Polypropyleneglycol = 'Polypropyleneglycol'
}